"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import Cookies from "js-cookie";

export const useCookie = (
  name: string,
  attributes?: Cookies.CookieAttributes & {
    defaultValue?: string;
    changeListener?: (
      value: string | undefined,
      setCookie: (value: string | undefined) => void
    ) => void;
  }
) => {
  const { defaultValue, changeListener, ...attrs } = useMemo(
    () => attributes || {},
    [attributes]
  );

  const [value, setValue] = useState<string | undefined>();

  const notifyChanged = useCallback(
    (newValue: string | undefined) => {
      if (newValue === value) {
        return;
      }
      const event = new CustomEvent(name + "Changed", { detail: { value: newValue } });
      document.dispatchEvent(event);
    },
    [name, value]
  );

  const setCookie = useCallback(
    (newValue: string | undefined) => {
      if (newValue === value) {
        return;
      }
      if (newValue === undefined) {
        Cookies.remove(name);
        setValue(undefined);
        notifyChanged(undefined);
      } else {
        Cookies.set(name, newValue, attrs);
        setValue(newValue);
        notifyChanged(newValue);
      }
    },
    [name, attrs, notifyChanged, value]
  );

  const deleteCookie = useCallback(() => {
    setCookie(undefined);
  }, [setCookie]);

  useEffect(() => {
    const cookie = Cookies.get(name);
    if (cookie) {
      setValue(cookie);
      notifyChanged(cookie);
    }
    if (defaultValue) {
      Cookies.set(name, defaultValue, attrs);
      setValue(defaultValue);
      notifyChanged(defaultValue);
    }

    const listener = (e: Event) => {
      const event = e as CustomEvent<{ value: string }>;
      if (event.detail.value !== value && changeListener) {
        const newValue = event.detail.value;
        setTimeout(() => {
          changeListener(newValue, setCookie);
        }, 10);
      }
    };
    document.addEventListener(name + "Changed", listener);
    return () => {
      document.removeEventListener(name + "Changed", listener);
    };
  }, [defaultValue, attrs, name, notifyChanged, changeListener, value, setCookie]);

  return [value, setCookie, deleteCookie] as const;
};
